/**
 * ExerciseSelectorGrid - Reusable exercise selector with grid card layout
 * Used in FullScreenWorkoutEditor for both Template and Instance modes
 * Matches the card design from ExerciseLibrary (ExerciseManager.js)
 * 
 * INSTANCE MODE FEATURES:
 * - Shows usage data (last date, weight, reps) on exercise cards
 * - Prefill data is passed via exerciseUsage prop
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import { Search, X, Filter, Clock, ArrowUp, ArrowDown, ListChecks } from 'lucide-react';
import { getExerciseAnimationUrl } from '../../utils/exerciseImages';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../translations';
import { searchExercises } from '../../utils/exerciseSearch';
import { MOVEMENT_PATTERNS } from '../../utils/cardio';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format date for display
const formatUsageDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
};

// Category color mapping (same as ExerciseManager)
const getCategoryColor = (category) => {
  const colors = {
    chest: 'bg-red-500/25 text-red-300',
    back: 'bg-blue-500/25 text-blue-300',
    legs: 'bg-green-500/25 text-green-300',
    shoulders: 'bg-orange-500/25 text-orange-300',
    arms: 'bg-purple-500/25 text-purple-300',
    core: 'bg-yellow-500/25 text-yellow-300',
    cardio: 'bg-pink-500/25 text-pink-300',
    'full-body': 'bg-teal-500/25 text-teal-300',
    stretching: 'bg-cyan-500/25 text-cyan-300',
    plyometrics: 'bg-amber-500/25 text-amber-300',
  };
  return colors[category] || 'bg-gray-500/25 text-gray-300';
};

// Filter options (static labels — "all" option is translated inside component)
const CATEGORIES = [
  { value: 'all', labelKey: 'all_categories', fallback: 'All Categories' },
  { value: 'chest', label: 'Chest' },
  { value: 'back', label: 'Back' },
  { value: 'legs', label: 'Legs' },
  { value: 'shoulders', label: 'Shoulders' },
  { value: 'arms', label: 'Arms' },
  { value: 'core', label: 'Core' },
  { value: 'cardio', label: 'Cardio' },
  { value: 'full-body', label: 'Full Body' },
  { value: 'stretching', label: 'Stretching' },
  { value: 'plyometrics', label: 'Plyometrics' },
];

const PRIMARY_MUSCLES = [
  'Quadriceps', 'Glutes', 'Hamstrings', 'Calves', 
  'Chest', 'Back', 'Lats', 'Deltoids', 
  'Biceps', 'Triceps', 'Forearms', 
  'Abs', 'Obliques', 'Core', 'Trapezius', 'Rhomboids'
];

const ExerciseSelectorGrid = ({ 
  exercises = [], 
  onSelectExercise, 
  onClose, 
  isTemplateMode = false,
  exerciseUsage = {},  // Client's exercise history {exercise_id: {last_date, last_weight, last_reps, use_count}}
  t: tProp,
  // Optional "in this plan" left column: pass the ordered list of chosen
  // exercises + reorder/remove callbacks to show and control them live.
  selectedExercises = null,
  onMoveSelected,
  onRemoveSelected,
  fullScreen = false,
}) => {
  // Resilient translator: prefer the caller's `t` only when it actually
  // resolves a key; otherwise translate via the language context. This fixes
  // callers that pass nothing or an identity fn (e.g. the planner editor),
  // which previously rendered raw i18n keys ("select_exercise", etc.).
  const { language } = useLanguage();
  const { t: ctxT } = useTranslation(language);
  const t = (key) => {
    if (typeof tProp === 'function') {
      const v = tProp(key);
      if (v && v !== key) return v;
    }
    return ctxT(key);
  };
  
  // "In this plan" left column data (optional).
  const hasSelectedColumn = Array.isArray(selectedExercises);
  const selEx = hasSelectedColumn ? selectedExercises : [];
  const exId = (ex) => ex?.exercise_id || ex?.id;
  const exName = (ex) => ex?.exercise_name || ex?.name || ex?.exercise_name_es || 'Exercise';
  const selectedIds = new Set(selEx.map(exId).filter(Boolean));

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [muscleFilter, setMuscleFilter] = useState('all');
  const [equipmentFilter, setEquipmentFilter] = useState('all');
  const [movementPatternFilter, setMovementPatternFilter] = useState('all');
  const [equipmentList, setEquipmentList] = useState([]);
  const [sortMode, setSortMode] = useState('recent'); // recent | most_used | least_used
  const [showFilters, setShowFilters] = useState(true);

  // Load canonical equipment reference for the Equipment filter dropdown.
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/reference/equipment`)
      .then((res) => { if (alive) setEquipmentList(res.data?.equipment || res.data || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  
  // Track recently added exercises for visual feedback
  const [recentlyAdded, setRecentlyAdded] = useState(new Set());

  // Lazy load — render PAGE_SIZE cards at a time and grow as user scrolls.
  // No artificial cap; the full filtered list is reachable by scrolling.
  const PAGE_SIZE = 60;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Handle exercise selection - stays open, shows feedback
  const handleSelectExercise = (exercise) => {
    // Add to workout
    onSelectExercise(exercise);
    
    // Show visual feedback - add to recently added set
    setRecentlyAdded(prev => new Set([...prev, exercise.id]));
    
    // Remove from recently added after 2 seconds (for animation reset)
    setTimeout(() => {
      setRecentlyAdded(prev => {
        const next = new Set(prev);
        next.delete(exercise.id);
        return next;
      });
    }, 2000);
  };

  // Filtered exercises using same logic as ExerciseLibrary
  const filteredExercises = useMemo(() => {
    // Search filter (language-aware, accent-normalized, tokenized AND)
    let filtered = searchExercises(searchTerm, exercises, 'es');
    
    filtered = filtered.filter(exercise => {
      // Category filter
      if (categoryFilter !== 'all' && exercise.category !== categoryFilter) {
        return false;
      }
      
      // Muscle filter
      if (muscleFilter !== 'all') {
        const muscles = exercise.primary_muscle_groups || exercise.muscle_groups || [];
        if (!muscles.includes(muscleFilter)) return false;
      }
      
      // Equipment filter (canonical equipment_ids, matches the library)
      if (equipmentFilter !== 'all') {
        const ids = exercise.equipment_ids || [];
        if (!ids.includes(equipmentFilter)) return false;
      }

      // Movement pattern filter
      if (movementPatternFilter !== 'all' && exercise.movement_pattern !== movementPatternFilter) {
        return false;
      }

      return true;
    });

    // Predefined ordering for the client planner: surface exercises this client
    // has actually performed. Only when NOT actively searching (search keeps
    // its relevance order) and not in template mode.
    if (!isTemplateMode && !searchTerm.trim() && exerciseUsage && Object.keys(exerciseUsage).length) {
      const countOf = (id) => exerciseUsage[id]?.use_count || 0;
      if (sortMode === 'most_used') {
        // Most-used first; unused (count 0) fall to the bottom keeping order.
        filtered = [...filtered].sort((a, b) => {
          const ca = countOf(a.id);
          const cb = countOf(b.id);
          if (ca === cb) return 0;
          if (!ca) return 1;
          if (!cb) return -1;
          return cb - ca;
        });
      } else if (sortMode === 'least_used') {
        // Least-used first among PERFORMED exercises only (count > 0);
        // never-performed exercises (count 0) are pushed to the bottom.
        filtered = [...filtered].sort((a, b) => {
          const ca = countOf(a.id);
          const cb = countOf(b.id);
          if (ca === cb) return 0;
          if (!ca) return 1;
          if (!cb) return -1;
          return ca - cb;
        });
      } else {
        // Default: most-recently used first, then the rest.
        filtered = [...filtered].sort((a, b) => {
          const da = exerciseUsage[a.id]?.last_date || '';
          const db_ = exerciseUsage[b.id]?.last_date || '';
          if (da && db_) return db_.localeCompare(da);   // both used → most recent first
          if (da) return -1;                              // used before unused
          if (db_) return 1;
          return 0;                                       // keep existing order for the rest
        });
      }
    }

    return filtered;
  }, [exercises, searchTerm, categoryFilter, muscleFilter, equipmentFilter, movementPatternFilter, isTemplateMode, exerciseUsage, sortMode]);

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setMuscleFilter('all');
    setEquipmentFilter('all');
    setMovementPatternFilter('all');
  };

  const hasActiveFilters = searchTerm || categoryFilter !== 'all' || muscleFilter !== 'all' || equipmentFilter !== 'all' || movementPatternFilter !== 'all';

  // Reset lazy window when the filtered result set changes (filter/search input).
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [searchTerm, categoryFilter, muscleFilter, equipmentFilter, movementPatternFilter, sortMode]);

  // IntersectionObserver — when the sentinel scrolls into view, page in the
  // next PAGE_SIZE cards. The root is the scrollable container so we don't
  // depend on the viewport.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollContainerRef.current;
    if (!sentinel || !root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisibleCount((prev) => prev + PAGE_SIZE);
        }
      },
      { root, rootMargin: '300px 0px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const accentColor = isTemplateMode ? 'purple' : 'blue';
  
  // Use explicit class names for Tailwind JIT compatibility
  const accentClasses = isTemplateMode ? {
    container: 'bg-purple-900/30 border-purple-500/30',
    header: 'bg-purple-900/50 border-purple-500/20',
    text: 'text-purple-300',
    textLight: 'text-purple-200',
    filterActive: 'bg-purple-500/30 text-purple-200',
    cardHover: 'hover:border-purple-400/50 hover:shadow-purple-500/20',
    overlay: 'from-purple-900/90 via-purple-900/40',
    button: 'bg-purple-500'
  } : {
    container: 'bg-blue-900/30 border-blue-500/30',
    header: 'bg-blue-900/50 border-blue-500/20',
    text: 'text-blue-300',
    textLight: 'text-blue-200',
    filterActive: 'bg-blue-500/30 text-blue-200',
    cardHover: 'hover:border-blue-400/50 hover:shadow-blue-500/20',
    overlay: 'from-blue-900/90 via-blue-900/40',
    button: 'bg-blue-500'
  };

  return (
    <div className={`${accentClasses.container} border overflow-hidden ${fullScreen ? 'h-full flex flex-col rounded-none sm:rounded-xl' : 'rounded-xl'}`}>
      {/* Header */}
      <div className={`${accentClasses.header} px-4 py-3 border-b`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className="text-white font-semibold text-lg">{t('select_exercise') || 'Select Exercise'}</h3>
            <span className={`${accentClasses.text} text-sm`}>
              {filteredExercises.length} {t('of') || 'of'} {exercises.length} {t('exercises') || 'exercises'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg transition ${showFilters ? accentClasses.filterActive : 'bg-gray-700/50 text-gray-400'}`}
              title={showFilters ? 'Hide filters' : 'Show filters'}
            >
              <Filter className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 hover:text-white transition"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className={`flex flex-col sm:flex-row ${fullScreen ? 'flex-1 min-h-0' : ''}`}>
        {/* "In this plan" column — always-visible selected list with reorder */}
        {hasSelectedColumn && (
          <aside
            className={`sm:w-64 sm:flex-shrink-0 border-b sm:border-b-0 sm:border-r border-gray-700/50 bg-gray-900/40 flex flex-col ${fullScreen ? 'max-h-[35vh] sm:max-h-none' : 'max-h-[40vh] sm:max-h-[62vh]'}`}
            data-testid="selector-plan-list"
          >
            <div className="px-3 py-2 border-b border-gray-700/50 flex items-center gap-2 sticky top-0 bg-gray-900/70">
              <ListChecks className="w-4 h-4 text-blue-300" />
              <span className="text-white text-sm font-semibold">In this plan</span>
              <span className="text-gray-400 text-xs">({selEx.length})</span>
            </div>
            <div className="overflow-y-auto p-2 space-y-1.5 flex-1">
              {selEx.length === 0 ? (
                <p className="text-gray-500 text-xs px-1 py-3 text-center">No exercises yet — add from the right.</p>
              ) : (
                selEx.map((ex, index) => (
                  <div
                    key={`${exId(ex)}-${index}`}
                    className="flex items-center gap-1.5 bg-slate-800/70 border border-gray-700/60 rounded-lg px-2 py-1.5"
                    data-testid={`plan-item-${index}`}
                  >
                    <span className="text-gray-500 text-xs font-mono w-4 flex-shrink-0">{index + 1}</span>
                    <span className="text-white text-xs font-medium truncate flex-1 min-w-0" title={exName(ex)}>{exName(ex)}</span>
                    {onMoveSelected && (
                      <div className="flex flex-col flex-shrink-0">
                        <button
                          onClick={() => onMoveSelected(index, -1)}
                          disabled={index === 0}
                          className="text-gray-400 hover:text-white disabled:opacity-25 disabled:cursor-not-allowed"
                          title="Move up"
                          data-testid={`plan-up-${index}`}
                        >
                          <ArrowUp className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => onMoveSelected(index, 1)}
                          disabled={index === selEx.length - 1}
                          className="text-gray-400 hover:text-white disabled:opacity-25 disabled:cursor-not-allowed"
                          title="Move down"
                          data-testid={`plan-down-${index}`}
                        >
                          <ArrowDown className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                    {onRemoveSelected && (
                      <button
                        onClick={() => onRemoveSelected(index)}
                        className="text-gray-400 hover:text-red-400 flex-shrink-0"
                        title="Remove"
                        data-testid={`plan-remove-${index}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </aside>
        )}

        <div className={`flex-1 min-w-0 ${fullScreen ? 'flex flex-col min-h-0' : ''}`}>
      {/* Category quick-filter chips — always visible one-tap filter */}
      <div className="flex items-center gap-1.5 flex-wrap px-4 pt-3" data-testid="category-quick-filters">
        {CATEGORIES.map(cat => {
          const active = categoryFilter === cat.value;
          const label = cat.value === 'all' ? (t('all') || 'All') : (cat.labelKey ? (t(cat.labelKey) || cat.fallback) : cat.label);
          return (
            <button
              key={cat.value}
              type="button"
              onClick={() => setCategoryFilter(cat.value)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${active ? `${accentClasses.button} text-white border-transparent` : 'bg-slate-700/50 text-gray-300 border-gray-600/40 hover:bg-slate-600/50'}`}
              data-testid={`category-chip-${cat.value}`}
            >
              {label}
            </button>
          );
        })}
      </div>
      {showFilters && (
        <div className={`px-4 py-3 ${isTemplateMode ? 'bg-purple-900/20 border-purple-500/10' : 'bg-blue-900/20 border-blue-500/10'} border-b`}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-2">
            {/* Search */}
            <div className="lg:col-span-2">
              <label className={`block ${accentClasses.textLight} text-xs font-medium mb-1`}>Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('search_exercises') || 'Search exercises...'}
                  className="input-field pl-9 text-sm w-full"
                  data-testid="exercise-search-input"
                />
              </div>
            </div>

            {/* Category */}
            <div>
              <label className={`block ${accentClasses.textLight} text-xs font-medium mb-1`}>Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="input-field text-sm w-full"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.labelKey ? (t(cat.labelKey) || cat.fallback) : cat.label}</option>
                ))}
              </select>
            </div>

            {/* Muscle */}
            <div>
              <label className={`block ${accentClasses.textLight} text-xs font-medium mb-1`}>Muscle</label>
              <select
                value={muscleFilter}
                onChange={(e) => setMuscleFilter(e.target.value)}
                className="input-field text-sm w-full"
              >
                <option value="all">{t('all_muscles') || 'All Muscles'}</option>
                {PRIMARY_MUSCLES.map(muscle => (
                  <option key={muscle} value={muscle}>{muscle}</option>
                ))}
              </select>
            </div>

            {/* Equipment */}
            <div>
              <label className={`block ${accentClasses.textLight} text-xs font-medium mb-1`}>Equipment</label>
              <select
                value={equipmentFilter}
                onChange={(e) => setEquipmentFilter(e.target.value)}
                className="input-field text-sm w-full"
                data-testid="exercise-equipment-select"
              >
                <option value="all">{t('all_equipment') || 'All Equipment'}</option>
                {equipmentList.map(eq => (
                  <option key={eq.id} value={eq.id}>{language === 'es' ? (eq.name_es || eq.name) : eq.name}</option>
                ))}
              </select>
            </div>

            {/* Movement Pattern filter */}
          <div>
            <label className={`block ${accentClasses.textLight} text-xs font-medium mb-1`}>Movement</label>
            <select
              value={movementPatternFilter}
              onChange={(e) => setMovementPatternFilter(e.target.value)}
              className="input-field text-sm w-full"
              data-testid="exercise-movement-select"
            >
              <option value="all">All Patterns</option>
              {MOVEMENT_PATTERNS.map(mp => (
                <option key={mp.id} value={mp.id}>{mp.name}</option>
              ))}
            </select>
          </div>

          {/* Sort by usage (INSTANCE MODE ONLY) */}
            {!isTemplateMode && (
              <div>
                <label className={`block ${accentClasses.textLight} text-xs font-medium mb-1`}>Sort by</label>
                <select
                  value={sortMode}
                  onChange={(e) => setSortMode(e.target.value)}
                  className="input-field text-sm w-full"
                  data-testid="exercise-sort-select"
                >
                  <option value="recent">Recently used</option>
                  <option value="most_used">Most used</option>
                  <option value="least_used">Least used</option>
                </select>
              </div>
            )}
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <div className="mt-3 flex justify-end">
              <button
                onClick={clearFilters}
                className="text-sm px-3 py-1.5 rounded-lg bg-gray-600/40 text-gray-200 hover:bg-gray-500/40 transition"
              >
                {t('clear_filters') || 'Clear Filters'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Exercise Grid */}
      <div ref={scrollContainerRef} className={`p-4 overflow-y-auto ${fullScreen ? 'flex-1 min-h-0' : 'max-h-[56vh]'}`}>
        {filteredExercises.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg mb-2">{t('no_exercises_found') || 'No exercises found'}</p>
            <p className="text-gray-500 text-sm">{t('clear_filters') || 'Try adjusting your filters'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredExercises.slice(0, visibleCount).map((exercise) => {
              const isJustAdded = recentlyAdded.has(exercise.id);
              const isInPlan = selectedIds.has(exercise.id);
              // Get usage data for this exercise (instance mode only)
              const usage = !isTemplateMode ? exerciseUsage[exercise.id] : null;
              // Get client feedback for this exercise
              const feedback = usage?.feedback || null;
              
              return (
                <button
                  key={exercise.id}
                  type="button"
                  onClick={() => handleSelectExercise(exercise)}
                  className={`group relative bg-slate-800/60 rounded-xl overflow-hidden text-left
                    border-2 transition-all duration-300
                    ${isJustAdded 
                      ? 'border-green-400 shadow-lg shadow-green-500/30 scale-[0.98]' 
                      : feedback === 'dislike'
                        ? `border-red-500/40 ${accentClasses.cardHover} hover:scale-[1.02] hover:shadow-lg`
                        : feedback === 'favorite'
                          ? `border-green-500/30 ${accentClasses.cardHover} hover:scale-[1.02] hover:shadow-lg`
                          : `border-transparent ${accentClasses.cardHover} hover:scale-[1.02] hover:shadow-lg`
                    }`}
                  data-testid={`exercise-card-${exercise.id}`}
                >
                  {/* Added Badge */}
                  {isInPlan && !isJustAdded && (
                    <div className="absolute top-2 right-2 z-20 bg-blue-500/90 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`in-plan-badge-${exercise.id}`}>
                      <span>✓ {t('in_plan') || 'In plan'}</span>
                    </div>
                  )}
                  {isJustAdded && (
                    <div className="absolute top-2 right-2 z-20 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1 animate-pulse">
                      <span>{t('just_added') || 'Added'}</span>
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}

                  {/* Usage Badge - INSTANCE MODE ONLY */}
                  {usage && !isJustAdded && (
                    <div className="absolute top-2 left-2 z-10 bg-blue-600/90 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1" data-testid={`exercise-usage-badge-${exercise.id}`}>
                      <Clock className="w-3 h-3" />
                      <span>{formatUsageDate(usage.last_date)}</span>
                      {usage.use_count > 1 && (
                        <span className="pl-1 ml-0.5 border-l border-white/30 font-semibold" title={`Realizado ${usage.use_count} veces`}>
                          ×{usage.use_count}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Client Feedback Badge - Shows thumbs up/down */}
                  {feedback && !isJustAdded && (
                    <div className={`absolute top-2 right-2 z-10 text-lg leading-none px-1.5 py-1 rounded-md ${
                      feedback === 'favorite' 
                        ? 'bg-green-600/90' 
                        : 'bg-red-600/90'
                    }`} data-testid={`exercise-feedback-${exercise.id}`} title={feedback === 'favorite' ? 'Client likes this exercise' : 'Client dislikes this exercise'}>
                      {feedback === 'favorite' ? '👍' : '👎'}
                    </div>
                  )}

                  {/* Image Container */}
                  <div className="relative aspect-square bg-slate-900/50 overflow-hidden">
                    {(exercise.animation_webp || exercise.image_url) ? (
                      <img
                        src={getExerciseAnimationUrl(exercise)}
                        alt={exercise.name}
                        className={`w-full h-full object-cover transition-all duration-300 
                          ${isJustAdded ? 'opacity-70' : 'group-hover:scale-105'}`}
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-5xl opacity-30">🏋️</span>
                      </div>
                    )}

                    {/* Hover Overlay - hidden when just added */}
                    {!isJustAdded && (
                      <div className={`absolute inset-0 bg-gradient-to-t ${accentClasses.overlay} to-transparent 
                        opacity-0 group-hover:opacity-100 transition-opacity duration-200 
                        flex items-center justify-center`}>
                        <div className={`${accentClasses.button} text-white px-4 py-2 rounded-lg font-medium text-sm 
                          transform translate-y-2 group-hover:translate-y-0 transition-transform duration-200`}>
                          + {t('add_to_workout') || 'Add'}
                        </div>
                      </div>
                    )}

                    {/* Added Overlay */}
                    {isJustAdded && (
                      <div className="absolute inset-0 bg-green-900/40 flex items-center justify-center">
                        <div className="bg-green-500 text-white px-4 py-2 rounded-lg font-medium text-sm">
                          ✓ Added!
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Card Content */}
                  <div className="p-3">
                    {/* Exercise Name */}
                    <h4 className={`font-semibold text-sm leading-tight mb-2 line-clamp-2 transition-colors
                      ${isJustAdded ? 'text-green-200' : 'text-white group-hover:text-blue-200'}`}>
                      {exercise.name}
                    </h4>

                    {/* Usage Data - Last Set (INSTANCE MODE ONLY).
                        The "when" is shown by the date badge over the image
                        (formatUsageDate), which already blends free + scheduled
                        routines via the client-usage endpoint. */}
                    {usage && usage.last_weight > 0 && (
                      <div className="text-xs text-blue-300 mb-1.5 font-medium">
                        {t('last_used_label') || 'Last'}: {usage.last_reps}x{usage.last_weight}kg
                      </div>
                    )}

                    {/* Badges */}
                    <div className="flex flex-wrap gap-1">
                      {/* Category Badge */}
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getCategoryColor(exercise.category)}`}>
                        {exercise.category}
                      </span>

                      {/* Primary Muscles (max 1 for compact view) */}
                      {(exercise.primary_muscle_groups || exercise.muscle_groups || []).slice(0, 1).map((muscle, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300"
                        >
                          {muscle}
                        </span>
                      ))}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Lazy-load sentinel + status footer.
            - When more cards are available, the sentinel is observed and
              triggers paging in the next PAGE_SIZE cards.
            - When everything is rendered, show a subtle "end of list" line. */}
        {filteredExercises.length > 0 && (
          visibleCount < filteredExercises.length ? (
            <div ref={sentinelRef} className="py-4 text-center" data-testid="exercise-grid-sentinel">
              <p className="text-gray-500 text-xs">
                {t('loading_more') || 'Cargando más…'} ({visibleCount} / {filteredExercises.length})
              </p>
            </div>
          ) : (
            <div className="py-3 text-center" data-testid="exercise-grid-end">
              <p className="text-gray-600 text-xs">
                {filteredExercises.length} {t('exercises') || 'ejercicios'}
              </p>
            </div>
          )
        )}
      </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseSelectorGrid;
